/* eslint-disable no-console */
import {FC, useState} from "react";
import {TextInput} from "@interstate/components/TextInput";
import {useOperationalEntity} from "../../context";
import {useLazyQuery, useMutation} from "@apollo/client";
import {
  LookupOperationalEntity,
  UpdateOperationalEntityParentData,
  UpdateOperationalEntityParent,
  UpdateOperationalEntityParentVariables,
  LookupOperationalEntityData,
  LookupOperationalEntityVariables,
  assertOperationalEntityPresent
} from "../../backend";
import {UpdateParentInput} from "@common-core/coat-operational-hierarchy-appsync-model";
import {Endpoints} from "../../../runtime";
import {useTranslation} from "react-i18next";
import {empty, ErrorHandler, OnCompleteCallback} from "../../../utils";
import {ModalWithAlert, useAlert} from "../../../alerting";
import "./EditParentModal.scss";

export const EditParentModal: FC<{
  id: string;
  currentParent?: string;
  onComplete: OnCompleteCallback;
}> = ({id, currentParent, onComplete}) => {
  const {t} = useTranslation();
  const {alert, clearAlert, createErrorAlert} = useAlert({
    errorTitleKey: "toast.backend-error"
  });
  const [inputErrorMessage, setInputErrorMessage] = useState<string>();
  const [working, setWorking] = useState(false);
  const [value, setValue] = useState(currentParent);
  const {entity, setEntity} = useOperationalEntity();

  const [lookUpOperationalEntity] = useLazyQuery<
    LookupOperationalEntityData,
    LookupOperationalEntityVariables
  >(LookupOperationalEntity, {
    context: {
      endpoint: Endpoints.APPSYNC
    },
    fetchPolicy: "network-only"
  });

  const [updateParent] = useMutation<
    UpdateOperationalEntityParentData,
    UpdateOperationalEntityParentVariables
  >(UpdateOperationalEntityParent, {
    context: {
      endpoint: Endpoints.APPSYNC
    }
  });

  const clearErrors = (): void => {
    clearAlert();
    setInputErrorMessage(undefined);
  };

  const handleError: ErrorHandler = (error: any) => {
    // TODO: We need a generic mechanism to indicate validation-related errors along with a reason-code
    error.message.includes("parent id:")
      ? setInputErrorMessage(error.message)
      : createErrorAlert(error);
  };

  const refreshEntity = async (): Promise<void> => {
    return await lookUpOperationalEntity({variables: {id: entity.id}})
      .then(assertOperationalEntityPresent)
      .then(setEntity);
  };

  const saveChanges = async () => {
    setWorking(true);
    clearErrors();
    const parentInput: UpdateParentInput = {
      parentId: value,
      entityId: id
    };
    await updateParent({variables: {parentInput}})
      .then(result => {
        if (result.data?.entity.success === false) {
          throw new Error(t("common-errors.backend-error"));
        }
      })
      .then(refreshEntity)
      // If the update call returns an error, give the user the opportunity to try again
      .then(() => onComplete(true))
      .catch(handleError)
      .finally(() => setWorking(false));
  };

  const cancelChanges = () => {
    onComplete(false);
  };

  const onInputChange = (event: any) => {
    setValue(event.target?.value.trim());
  };

  return (
    <ModalWithAlert
      id={"edit-parent-modal"}
      data-testid={"edit-parent-modal"}
      show={true}
      size={"large"}
      header={t("modal.header.edit-parent")}
      alert={alert}
      footer={{
        primary: {
          action: saveChanges,
          label: t("common-actions.save-changes"),
          isLoading: working
        },
        options: {
          action: cancelChanges,
          label: t("common-actions.cancel"),
          isLoading: working,
          buttonStyle: "tertiary"
        }
      }}
      onHide={cancelChanges}>
      <TextInput
        id={"parent-text-input"}
        data-testid={"parent-text-input"}
        name={"parent"}
        label={`${t("modal.text-input.edit-parent")}`}
        value={value}
        onChange={onInputChange}
        onKeyDown={async (event: any) => {
          if (event.key === "Enter") {
            await saveChanges();
          }
        }}
        clearButton={!empty(value)}
        hasError={!!inputErrorMessage}
        errorMessage={inputErrorMessage}
      />
    </ModalWithAlert>
  );
};
